import Button from '@superfunnel/ui-core/v2/Button';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { Check, NavArrowDown, NavArrowRight, Xmark } from 'iconoir-react';
import { useState } from 'react';
import { AgentChange } from '../../types/agentChanges';

interface AgentChangesNotificationProps {
  disabled?: boolean;
  changes: AgentChange[];
  onChangeClick?: (changeId: string) => void;
  onAcceptChange: (changeId: string) => void;
  onDiscardChange: (changeId: string) => void;
  onAcceptAll: () => void;
  onDiscardAll: () => void;
}

export function AgentChangesNotification({
  disabled,
  changes,
  onChangeClick,
  onAcceptChange,
  onDiscardChange,
  onAcceptAll,
  onDiscardAll,
}: AgentChangesNotificationProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasPendingChanges =
    changes.filter((change) => change.status === 'pending').length > 0;

  // const getChangeTypeIcon = (type?: string) => {
  //   switch (type) {
  //     case 'modified':
  //       return <EditPencil className="size-4 text-blue-500" />;
  //     case 'created':
  //       return <Plus className="size-4 text-green-500" />;
  //     case 'deleted':
  //       return <Minus className="size-4 text-red-500" />;
  //     default:
  //       return null;
  //   }
  // };

  if (changes.length === 0) return null;

  return (
    <div className="p-2">
      {/* Header with summary and bulk actions */}
      <div
        className="flex cursor-pointer flex-col rounded-xl bg-white py-2"
        style={{ boxShadow: '0px 1px 2px 0px rgba(21, 28, 36, 0.05)' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-2 px-2">
          <div className="flex items-center justify-center gap-1">
            <div className="flex size-8 shrink-0 flex-col items-center justify-center">
              {isExpanded ? (
                <NavArrowDown className="size-5" />
              ) : (
                <NavArrowRight className="size-5" />
              )}
            </div>

            <span className="text-base-900 text-sm leading-normal font-normal tracking-tight">
              Agent changed {changes.length} section
              {changes.length > 1 ? 's' : ''}
            </span>
          </div>

          {hasPendingChanges && (
            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    size="sm"
                    variant={'outline'}
                    color="white"
                    disabled={disabled}
                    onClick={onDiscardAll}
                  >
                    Discard
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>
                    {disabled
                      ? 'Wait for agent to finish'
                      : 'Discard all changes'}
                  </p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    size="sm"
                    color="positive"
                    disabled={disabled}
                    onClick={onAcceptAll}
                  >
                    Accept
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>
                    {disabled
                      ? 'Wait for agent to finish'
                      : 'Accept all changes'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>

        <div
          className={`grid transition-all duration-300 ease-in-out ${
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="px-2 pt-2">
              <Separator />
            </div>
            <div className="divide-base-300 mx-1 mt-2 max-h-72 divide-y overflow-clip overflow-y-auto rounded-md">
              {changes.map((change) => (
                <div
                  key={change.id}
                  className="hover:bg-base-50 flex min-h-10 items-center justify-between py-1 ps-1.5 pe-1"
                  onClick={(e) => {
                    if (onChangeClick) {
                      e.stopPropagation();
                      onChangeClick(change.id);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {/* <div>{change.type && getChangeTypeIcon(change.type)}</div> */}
                    <span className="text-sm text-gray-700">
                      {change.title}
                    </span>
                  </div>

                  <div className="flex gap-1">
                    {change.status === 'pending' && (
                      <>
                        <div className="flex">
                          {change.status === 'pending' && (
                            <>
                              <Tooltip open={!disabled ? false : undefined}>
                                <TooltipTrigger>
                                  <Button
                                    size={'icon-lg'}
                                    variant="ghost"
                                    color="negative"
                                    className="hover:bg-red-100"
                                    disabled={disabled}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDiscardChange(change.id);
                                    }}
                                  >
                                    <Xmark />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>
                                    {disabled
                                      ? 'Wait for agent to finish'
                                      : 'Discard change'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip open={!disabled ? false : undefined}>
                                <TooltipTrigger>
                                  <Button
                                    size={'icon-lg'}
                                    variant="ghost"
                                    color="positive"
                                    className="hover:bg-green-100"
                                    disabled={disabled}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onAcceptChange(change.id);
                                    }}
                                  >
                                    <Check />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>
                                    {disabled
                                      ? 'Wait for agent to finish'
                                      : 'Accept change'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    {change.status === 'accepted' && (
                      <span className="pe-2 text-xs text-gray-500">
                        Accepted
                      </span>
                    )}
                    {change.status === 'discarded' && (
                      <span className="pe-2 text-xs text-gray-500">
                        Discarded
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
